import React, { useState } from 'react';
import './Onboarding.css';

const Onboarding = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    skinType: '',
    ageRange: '',
    name: '',
    concerns: [],
    products: []
  });

  const skinTypes = [
    {
      id: 'dry',
      name: 'Dry',
      description: 'Feels tight, flaky, or rough',
      icon: '🏜️'
    },
    {
      id: 'oily',
      name: 'Oily',
      description: 'Shiny, enlarged pores, prone to breakouts',
      icon: '✨'
    },
    {
      id: 'combination',
      name: 'Combination',
      description: 'Oily T-zone, dry cheeks',
      icon: '🌓'
    },
    {
      id: 'sensitive',
      name: 'Sensitive',
      description: 'Easily irritated, reactive to products',
      icon: '🌸'
    },
    {
      id: 'normal',
      name: 'Normal',
      description: 'Balanced, few imperfections',
      icon: '🌿'
    }
  ];

  const ageRanges = [
    { id: '20s', name: '20s', icon: '🌺' },
    { id: '30s', name: '30s', icon: '🌹' },
    { id: '40s+', name: '40s+', icon: '🌻' }
  ];

  const skinConcerns = [
    { id: 'dryness', name: 'Dryness', icon: '💧', description: 'Tight, flaky, or dehydrated skin' },
    { id: 'oily-pores', name: 'Oily/Large Pores', icon: '✨', description: 'Excess oil and visible pores' },
    { id: 'acne', name: 'Acne/Breakouts', icon: '🎯', description: 'Pimples, blackheads, or blemishes' },
    { id: 'aging', name: 'Fine Lines/Aging', icon: '⏰', description: 'Wrinkles, loss of firmness' },
    { id: 'dark-spots', name: 'Dark Spots', icon: '🌟', description: 'Hyperpigmentation, uneven tone' },
    { id: 'dullness', name: 'Dullness/Want Glow', icon: '✨', description: 'Lack of radiance, tired-looking skin' },
    { id: 'sensitivity', name: 'Sensitivity/Redness', icon: '🌸', description: 'Irritation, redness, reactive skin' },
    { id: 'texture', name: 'Uneven Texture', icon: '🏔️', description: 'Rough, bumpy, or uneven skin surface' }
  ];

  const productOptions = [
    { id: 'moisturizer', name: 'Moisturizer', icon: '🧴', description: 'Daily hydrating cream' },
    { id: 'whitening-serum', name: 'Whitening Serum', icon: '✨', description: 'Brightening and spot treatment' },
    { id: 'moisturizing-serum', name: 'Moisturizing Serum', icon: '💧', description: 'Hydrating serum or essence' },
    { id: 'morning-cream', name: 'Morning Cream', icon: '🌅', description: 'Day cream with SPF' },
    { id: 'night-cream', name: 'Night Cream', icon: '🌙', description: 'Rich overnight treatment' },
    { id: 'cleanser', name: 'Cleanser', icon: '🧼', description: 'Face wash or cleansing foam' },
    { id: 'toner', name: 'Toner', icon: '💫', description: 'Balancing or hydrating toner' },
    { id: 'sunscreen', name: 'Sunscreen', icon: '☀️', description: 'SPF protection' },
    { id: 'eye-cream', name: 'Eye Cream', icon: '👁️', description: 'Under-eye treatment' },
    { id: 'face-mask', name: 'Face Mask', icon: '🥒', description: 'Weekly treatment mask' },
    { id: 'exfoliator', name: 'Exfoliator', icon: '✨', description: 'Scrub or chemical exfoliant' },
    { id: 'anti-aging-serum', name: 'Anti-Aging Serum', icon: '⏰', description: 'Retinol or peptide treatment' }
  ];

  const handleNext = () => {
    if (step < 5) {
      setStep(step + 1);
    } else {
      onComplete(profile);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const updateProfile = (key, value) => {
    setProfile(prev => ({ ...prev, [key]: value }));
  };

  const handleConcernToggle = (concernId) => {
    const existingIndex = profile.concerns.findIndex(c => c.concern === concernId);

    if (existingIndex >= 0) {
      // Remove concern
      const newConcerns = profile.concerns.filter(c => c.concern !== concernId);
      setProfile(prev => ({ ...prev, concerns: newConcerns }));
    } else if (profile.concerns.length < 3) {
      // Add concern with next priority
      const newConcern = {
        concern: concernId,
        priority: profile.concerns.length + 1
      };
      setProfile(prev => ({ ...prev, concerns: [...prev.concerns, newConcern] }));
    }
  };

  const handleProductToggle = (productId) => {
    const existingProducts = profile.products || [];
    const existingIndex = existingProducts.findIndex(p => p.id === productId);

    if (existingIndex >= 0) {
      // Remove product
      const newProducts = existingProducts.filter(p => p.id !== productId);
      setProfile(prev => ({ ...prev, products: newProducts }));
    } else {
      // Add product
      const newProduct = {
        id: productId,
        selected: true
      };
      setProfile(prev => ({ ...prev, products: [...existingProducts, newProduct] }));
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return profile.name.trim().length > 0;
      case 2: return profile.skinType !== '';
      case 3: return profile.ageRange !== '';
      case 4: return profile.concerns.length > 0;
      case 5: return true; // Products step is optional
      default: return false;
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card fade-in">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(step / 5) * 100}%` }}
          ></div>
        </div>

        {step === 1 && (
          <div className="onboarding-step">
            <div className="step-header">
              <h1>Welcome to Pretty Habits ✨</h1>
              <p>Let's create your personalized skincare journey</p>
            </div>
            <div className="input-group">
              <label>What should we call you?</label>
              <input
                type="text"
                placeholder="Enter your name"
                value={profile.name}
                onChange={(e) => updateProfile('name', e.target.value)}
                className="name-input"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-step">
            <div className="step-header">
              <h2>What's your skin type?</h2>
              <p>This helps us personalize your routine</p>
            </div>
            <div className="options-grid">
              {skinTypes.map(type => (
                <button
                  key={type.id}
                  className={`option-card ${profile.skinType === type.id ? 'selected' : ''}`}
                  onClick={() => updateProfile('skinType', type.id)}
                >
                  <span className="option-icon">{type.icon}</span>
                  <h3>{type.name}</h3>
                  <p>{type.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-step">
            <div className="step-header">
              <h2>What's your age range?</h2>
              <p>Different ages need different care</p>
            </div>
            <div className="options-grid age-grid">
              {ageRanges.map(range => (
                <button
                  key={range.id}
                  className={`option-card ${profile.ageRange === range.id ? 'selected' : ''}`}
                  onClick={() => updateProfile('ageRange', range.id)}
                >
                  <span className="option-icon">{range.icon}</span>
                  <h3>{range.name}</h3>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="onboarding-step">
            <div className="step-header">
              <h2>What are your main skin concerns?</h2>
              <p>Select up to 3 concerns in order of priority</p>
            </div>
            <div className="concerns-grid">
              {skinConcerns.map(concern => {
                const selectedConcern = profile.concerns.find(c => c.concern === concern.id);
                const isSelected = !!selectedConcern;
                const priority = selectedConcern?.priority;

                return (
                  <button
                    key={concern.id}
                    className={`concern-card ${isSelected ? 'selected' : ''} ${profile.concerns.length >= 3 && !isSelected ? 'disabled' : ''}`}
                    onClick={() => handleConcernToggle(concern.id)}
                    disabled={profile.concerns.length >= 3 && !isSelected}
                  >
                    <span className="concern-icon">{concern.icon}</span>
                    <h3>{concern.name}</h3>
                    <p>{concern.description}</p>
                    {isSelected && (
                      <div className="priority-badge">
                        Priority {priority}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="selection-info">
              <p>{profile.concerns.length}/3 concerns selected</p>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="onboarding-step">
            <div className="step-header">
              <h2>What products do you currently use?</h2>
              <p>Select the products you have so we can build your routine around them</p>
            </div>
            <div className="products-grid">
              {productOptions.map(product => {
                const isSelected = profile.products?.some(p => p.id === product.id);

                return (
                  <button
                    key={product.id}
                    className={`product-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleProductToggle(product.id)}
                  >
                    <span className="product-icon">{product.icon}</span>
                    <h3>{product.name}</h3>
                    <p>{product.description}</p>
                    {isSelected && (
                      <div className="selected-badge">
                        ✓
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="products-info">
              <p>{profile.products?.length || 0} products selected</p>
              <p className="products-note">Don't worry if you don't have all products - we'll suggest alternatives!</p>
            </div>
          </div>
        )}

        <div className="onboarding-actions">
          {step > 1 && (
            <button className="btn-secondary" onClick={handleBack}>
              Back
            </button>
          )}
          <button 
            className="btn-primary" 
            onClick={handleNext}
            disabled={!canProceed()}
          >
            {step === 5 ? 'Start My Journey' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
